import { useRef } from "react"
import type { PointerEvent } from "react"

const TAP_WINDOW_MS = 400
const TAP_MOVE_TOLERANCE_PX = 10

// Counts taps by hand instead of reading `click.detail`: iOS Safari reports
// detail 1 for every synthesized touch click, so a native triple-click check
// never fires there. A tap must settle within a few pixels of where it began
// (a scroll fling is not a tap) and land outside buttons and links, so the
// gesture cannot double as three presses of a control that lives inside the
// tapped surface. Text fields count: tapping into the composer is the
// natural place to do it, and focusing one has no side effect to guard.
export function useTripleTap(onTripleTap: () => void) {
  const tapCountRef = useRef(0)
  const lastTapAtRef = useRef(0)
  const downPositionRef = useRef<{ x: number; y: number } | null>(null)

  const onPointerDown = (event: PointerEvent<HTMLElement>) => {
    downPositionRef.current = { x: event.clientX, y: event.clientY }
  }

  const onPointerUp = (event: PointerEvent<HTMLElement>) => {
    const down = downPositionRef.current
    downPositionRef.current = null
    if (!isTap(event, down)) {
      tapCountRef.current = 0
      return
    }

    const now = performance.now()
    const continuesSequence = now - lastTapAtRef.current <= TAP_WINDOW_MS
    tapCountRef.current = continuesSequence ? tapCountRef.current + 1 : 1
    lastTapAtRef.current = now

    if (tapCountRef.current === 3) {
      tapCountRef.current = 0
      onTripleTap()
    }
  }

  return { onPointerDown, onPointerUp }
}

function isTap(
  up: PointerEvent<HTMLElement>,
  down: { x: number; y: number } | null
) {
  if (!down) {
    return false
  }
  const moved =
    Math.abs(up.clientX - down.x) > TAP_MOVE_TOLERANCE_PX ||
    Math.abs(up.clientY - down.y) > TAP_MOVE_TOLERANCE_PX
  const onControl = (up.target as Element).closest("button, a, [role='button']")
  return !moved && !onControl
}
